/**
 * listening-exam-gen — 英語聽力卷「產生器」(核心交付,boss「要能出類似題目」)。
 *
 * 單一真相:docs/spec/LISTENING_EXAM_SPEC.md §7。從**池 + 範本**生一份新的同型卷
 * (像「出練習卷」那樣不斷生新卷),而不是寫死一份。§2 的 Review 2 = 範例之一。
 *
 * 鐵則 / 設計:
 *   - **seeded RNG**(別用 Math.random):同 seed → 同卷,可重現;UI「重抽」= 換 seed。
 *   - **lockdown 防壞題**(memory feedback_generator_source_lockdown,不打地鼠):
 *       1. 池白名單 + 硬規則寫進產生器(本檔)。
 *       2. `validateGeneratedExam` 驗證器(回錯誤清單,空 = 合法)。
 *       3. regression test 跑 200 seed 全過驗證(見 tests/learn/listening-exam-gen.test.ts)。
 *   - `audio.spoken` 永遠是「完整正確」字 / 句(干擾項只印不唸)。
 *
 * 學習/遊戲分離:純學習側,不觸碰任何戰鬥數值。
 */

import type {
  ListeningExam,
  ListeningPart,
  ListenCheckItem,
  ListenChooseItem,
  ListenMarkItem,
  ListenCircleItem,
} from './listening-exam-types';

// ───────────────────────────── 7.1 opts ─────────────────────────────

/** 各 Part 題數(預設 6/4/5/5)。 */
export interface PartCounts {
  A?: number;
  B?: number;
  C?: number;
  D?: number;
}

/** 可換的池(預設 = 翰林 Review 2 範圍,見 DEFAULT_POOLS)。 */
export interface Pools {
  jobs: string[];
  familyMale: string[];
  familyFemale: string[];
  /** Part A:真實字 minimal pair(只差一音素),產生器隨機選哪個當答案。 */
  minimalPairs: Array<[string, string]>;
  /** Part D 圈字二選一的易混淆對。 */
  confusablePairs: Array<[string, string]>;
}

/** `generateListeningExam` 選項。 */
export interface ListeningGenOpts {
  /** 可重現種子(同 seed 同卷);UI「重抽」= 換 seed。 */
  seed: number;
  /** 各 part 題數,預設 6/4/5/5。 */
  counts?: PartCounts;
  /** 可換範圍(預設下方翰林 Review 2 範圍)。 */
  pools?: Partial<Pools>;
  /** 卷 id(預設 `listening-<seed>`)。 */
  id?: string;
  /** 卷名(預設 `英語聽力卷 · 第 <seed> 回`)。 */
  title?: string;
}

// ───────────────────────────── 7.2 池(我方定稿)─────────────────────────────

/**
 * 預設池 = 翰林 Review 2 範圍(逐字照 spec §7.2,教育複審 2026-06-14 已過)。
 *
 * `minimalPairs` 是**人工策展閉集常數**:每對附 IPA 已確認恰差一音素。產生器**只能
 * 從這 18 對抽**,**禁止**用「差一字母」程式生成新對(拼字≠音韻,如 dog/dot 假對立)。
 * 新增對 → 教育專家標 IPA 後才寫進此表。
 */
export const DEFAULT_POOLS: Pools = {
  jobs: ['teacher', 'nurse', 'cook', 'doctor', 'student', 'farmer', 'driver', 'singer'],
  // 性別:male = brother/dad/grandpa/father;female = sister/mom/grandma/mother。
  familyMale: ['brother', 'dad', 'grandpa', 'father'],
  familyFemale: ['sister', 'mom', 'grandma', 'mother'],
  minimalPairs: [
    ['bug', 'bun'], // /bʌɡ/ /bʌn/ 尾 g/n
    ['run', 'rug'], // /rʌn/ /rʌɡ/ 尾 n/g
    ['hot', 'hop'], // /hɒt/ /hɒp/ 尾 t/p
    ['map', 'mat'], // /mæp/ /mæt/ 尾 p/t
    ['cat', 'cap'], // /kæt/ /kæp/ 尾 t/p
    ['pen', 'pet'], // /pɛn/ /pɛt/ 尾 n/t
    ['log', 'dog'], // /lɒɡ/ /dɒɡ/ 初 l/d(取代假對立 dog/dot)
    ['cup', 'cut'], // /kʌp/ /kʌt/ 尾 p/t
    ['box', 'fox'], // /bɒks/ /fɒks/ 初 b/f
    ['man', 'fan'], // /mæn/ /fæn/ 初 m/f
    ['sun', 'fun'], // /sʌn/ /fʌn/ 初 s/f
    ['ten', 'hen'], // /tɛn/ /hɛn/ 初 t/h
    ['big', 'pig'], // /bɪɡ/ /pɪɡ/ 初 b/p
    ['hat', 'cat'], // /hæt/ /kæt/ 初 h/k
    ['hot', 'hat'], // /hɒt/ /hæt/ 母 ɒ/æ
    ['bug', 'bag'], // /bʌɡ/ /bæɡ/ 母 ʌ/æ
    ['pen', 'pan'], // /pɛn/ /pæn/ 母 ɛ/æ
    ['bed', 'bad'], // /bɛd/ /bæd/ 母 ɛ/æ
  ],
  // 每對都有對應範本可放(無範本 = 孤兒,不可進池)。剔除 [his,her]/[this,that]。
  confusablePairs: [
    ["Who's", "What's"],
    ['Is', 'Are'],
    ["he's", "she's"],
    ['am', 'is'],
    ['met', 'meet'],
    ['he', 'she'],
  ],
};

// ───────────────────────────── seeded RNG ─────────────────────────────

/**
 * 確定性 RNG —— mulberry32(小而均勻,純函式可重現)。注入 seed,杜絕 Math.random。
 */
export function makeRng(seed: number): () => number {
  let a = seed >>> 0;
  return function next(): number {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** [0, n) 的整數。 */
function randInt(rng: () => number, n: number): number {
  return Math.floor(rng() * n);
}

/** 抽一個元素。 */
function pick<T>(rng: () => number, arr: readonly T[]): T {
  return arr[randInt(rng, arr.length)];
}

/** 不重複抽 n 個(Fisher–Yates 取前 n;n 超過長度則回全部洗牌)。 */
function sampleWithout<T>(rng: () => number, arr: readonly T[], n: number): T[] {
  const copy = arr.slice();
  for (let i = copy.length - 1; i > 0; i--) {
    const j = randInt(rng, i + 1);
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }
  return copy.slice(0, Math.min(n, copy.length));
}

/** 擲銅板。 */
function coin(rng: () => number): boolean {
  return rng() < 0.5;
}

// ───────────────────────── 主詞 / 代名詞輔助 ─────────────────────────

type Subj = 'he' | 'she';

/** 主詞 → 所有格短句(He's / She's)。 */
function possOf(subj: Subj): string {
  return subj === 'he' ? "He's" : "She's";
}

/** 主詞 → be 動詞回應(he is / she is)。 */
function beOf(subj: Subj): string {
  return subj === 'he' ? 'he is' : 'she is';
}

/** 主詞 → 否定回應(he's not / she's not)。 */
function notOf(subj: Subj): string {
  return subj === 'he' ? "he's not" : "she's not";
}

/** 性別相反主詞。 */
function flipSubj(subj: Subj): Subj {
  return subj === 'he' ? 'she' : 'he';
}

// ───────────────────────────── 7.3 各題型生成 ─────────────────────────────

/** Part A:不重複抽 N 對 minimal pair;每對隨機定答案 + 順序隨機。 */
function genPartA(rng: () => number, pools: Pools, n: number): ListenCheckItem[] {
  const pairs = sampleWithout(rng, pools.minimalPairs, n);
  return pairs.map((pair, i): ListenCheckItem => {
    // 隨機定哪個是答案(唸出的那個),再隨機排兩字順序。
    const answerWord = pick(rng, pair);
    const other = pair[0] === answerWord ? pair[1] : pair[0];
    const options: [string, string] = coin(rng)
      ? [answerWord, other]
      : [other, answerWord];
    const answerIndex: 0 | 1 = options[0] === answerWord ? 0 : 1;
    return {
      id: `a${i + 1}`,
      options,
      audio: { id: `a${i + 1}`, spoken: answerWord },
      answerIndex,
    };
  });
}

/** B 範本種類。 */
type BTemplate = 'is-subj' | 'are-you' | 'whos-subj';

/**
 * Part B:抽問句範本 + 主詞,三選項恰一個文法相符,代名詞兩兩不同 → 唯一解。
 * 三選項順序隨機。
 */
function genPartB(rng: () => number, pools: Pools, n: number): ListenChooseItem[] {
  const templates: BTemplate[] = ['is-subj', 'are-you', 'whos-subj'];
  const items: ListenChooseItem[] = [];
  for (let i = 0; i < n; i++) {
    const tmpl = pick(rng, templates);
    const subj: Subj = coin(rng) ? 'he' : 'she';
    let spoken: string;
    let correct: string;
    let distractors: [string, string];

    if (tmpl === 'is-subj') {
      const job = pick(rng, pools.jobs);
      spoken = `Is ${subj} a ${job}?`;
      // 正解 Yes/No 隨機;代名詞各異:正解用 subj、干擾用反向 subj + I'm。
      if (coin(rng)) {
        correct = `Yes, ${beOf(subj)}.`;
        distractors = [`Yes, ${beOf(flipSubj(subj))}.`, 'Yes, I am.'];
      } else {
        correct = `No, ${notOf(subj)}.`;
        distractors = [`No, ${notOf(flipSubj(subj))}.`, "No, I'm not."];
      }
    } else if (tmpl === 'are-you') {
      const job = pick(rng, pools.jobs);
      spoken = `Are you a ${job}?`;
      if (coin(rng)) {
        correct = 'Yes, I am.';
        distractors = ['Yes, he is.', 'Yes, she is.'];
      } else {
        correct = "No, I'm not.";
        distractors = ["No, he's not.", "No, she's not."];
      }
    } else {
      // whos-subj:Who's he/she? → 正解 He's/She's my {同性別家人};干擾用反向性別家人。
      spoken = `Who's ${subj}?`;
      const maleFam = pick(rng, pools.familyMale);
      const femaleFam = sampleWithout(rng, pools.familyFemale, 2);
      if (subj === 'he') {
        correct = `He's my ${maleFam}.`;
        distractors = [`She's my ${femaleFam[0]}.`, `She's my ${femaleFam[1] ?? femaleFam[0]}.`];
      } else {
        const femFam = pick(rng, pools.familyFemale);
        const maleFam2 = sampleWithout(rng, pools.familyMale, 2);
        correct = `She's my ${femFam}.`;
        distractors = [`He's my ${maleFam2[0]}.`, `He's my ${maleFam2[1] ?? maleFam2[0]}.`];
      }
    }

    // 三選項隨機排列,記錄正解新位置。
    const order = shuffle3(rng, [correct, distractors[0], distractors[1]]);
    const answerIndex = order.indexOf(correct);
    items.push({
      id: `b${i + 1}`,
      audio: { id: `b${i + 1}`, spoken },
      choices: order,
      answerIndex,
    });
  }
  return items;
}

/** 洗 3 個元素(seeded)。 */
function shuffle3(rng: () => number, arr: [string, string, string]): string[] {
  const copy = arr.slice();
  for (let i = copy.length - 1; i > 0; i--) {
    const j = randInt(rng, i + 1);
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }
  return copy;
}

/** C 範本種類。 */
type CTemplate = 'family-job' | 'is-family-job' | 'whos-subj' | 'sorry-late';

/**
 * Part C:抽一句當 printed;擲銅板 same/diff。diff 只換一個語意元素且文法仍正確。
 */
function genPartC(rng: () => number, pools: Pools, n: number): ListenMarkItem[] {
  const templates: CTemplate[] = ['family-job', 'is-family-job', 'whos-subj', 'sorry-late'];
  const items: ListenMarkItem[] = [];
  for (let i = 0; i < n; i++) {
    const tmpl = pick(rng, templates);
    let printed: string;
    let spoken: string;
    let answer: 'O' | 'X';
    const same = coin(rng);

    if (tmpl === 'sorry-late') {
      // 定句,固定 same(無可改的語意元素而文法仍對 → 永遠 ○,杜絕壞題)。
      printed = "Sorry, I'm late.";
      spoken = printed;
      answer = 'O';
    } else if (tmpl === 'family-job') {
      const subj: Subj = coin(rng) ? 'he' : 'she';
      const fam = pick(rng, subj === 'he' ? pools.familyMale : pools.familyFemale);
      const job = pick(rng, pools.jobs);
      printed = `My ${fam} is a ${job}.`;
      if (same) {
        spoken = printed;
        answer = 'O';
      } else {
        // 只換 JOB(語意改變、文法仍正確;避免性別矛盾)。
        const job2 = pickOther(rng, pools.jobs, job);
        spoken = `My ${fam} is a ${job2}.`;
        answer = 'X';
      }
    } else if (tmpl === 'is-family-job') {
      const subj: Subj = coin(rng) ? 'he' : 'she';
      const fam = pick(rng, subj === 'he' ? pools.familyMale : pools.familyFemale);
      const job = pick(rng, pools.jobs);
      printed = `Is your ${fam} a ${job}?`;
      if (same) {
        spoken = printed;
        answer = 'O';
      } else {
        const job2 = pickOther(rng, pools.jobs, job);
        spoken = `Is your ${fam} a ${job2}?`;
        answer = 'X';
      }
    } else {
      // whos-subj:Who's he/she? diff = 換主詞性別(he↔she),文法仍正確。
      const subj: Subj = coin(rng) ? 'he' : 'she';
      printed = `Who's ${subj}?`;
      if (same) {
        spoken = printed;
        answer = 'O';
      } else {
        spoken = `Who's ${flipSubj(subj)}?`;
        answer = 'X';
      }
    }

    items.push({
      id: `c${i + 1}`,
      printed,
      audio: { id: `c${i + 1}`, spoken },
      answer,
    });
  }
  return items;
}

/** 從陣列抽一個不等於 `not` 的元素。 */
function pickOther(rng: () => number, arr: readonly string[], not: string): string {
  const candidates = arr.filter((x) => x !== not);
  if (candidates.length === 0) return not;
  return pick(rng, candidates);
}

/** D 範本:挖一個空成二選一,spoken = 填正解的完整句(必字面含答案)。 */
interface DTemplateResult {
  before: string;
  options: [string, string];
  after: string;
  spoken: string;
  answerIndex: 0 | 1;
}

/**
 * Part D:抽一句範本 + 一個易混淆對(或 JOB 對),挖空成 options;spoken = 含正解
 * 的完整句;硬規則:spoken 必字面包含答案 option。
 */
function genPartD(rng: () => number, pools: Pools, n: number): ListenCircleItem[] {
  const items: ListenCircleItem[] = [];
  for (let i = 0; i < n; i++) {
    const built = buildDItem(rng, pools);
    items.push({
      id: `d${i + 1}`,
      before: built.before,
      options: built.options,
      after: built.after,
      audio: { id: `d${i + 1}`, spoken: built.spoken },
      answerIndex: built.answerIndex,
    });
  }
  return items;
}

/** D 範本種類。 */
type DKind = 'nice-meet' | 'whos-fam' | 'is-job-not' | 'i-am-job' | 'confusable';

function buildDItem(rng: () => number, pools: Pools): DTemplateResult {
  const kinds: DKind[] = ['nice-meet', 'whos-fam', 'is-job-not', 'i-am-job', 'confusable'];
  const kind = pick(rng, kinds);

  // 二選一的順序隨機 + 答案位置工具:given 正解字 + 干擾字,回 {options, answerIndex}。
  const place = (correct: string, distractor: string): { options: [string, string]; answerIndex: 0 | 1 } => {
    const options: [string, string] = coin(rng) ? [correct, distractor] : [distractor, correct];
    const answerIndex: 0 | 1 = options[0] === correct ? 0 : 1;
    return { options, answerIndex };
  };

  if (kind === 'nice-meet') {
    // Nice to ( met / meet ) you. → meet
    const { options, answerIndex } = place('meet', 'met');
    return { before: 'Nice to', options, after: 'you.', spoken: 'Nice to meet you.', answerIndex };
  }
  if (kind === 'whos-fam') {
    // B: She's my ( sister / mother ). → 用家人對,spoken 含正解
    const subj: Subj = coin(rng) ? 'he' : 'she';
    const correctFam = pick(rng, subj === 'he' ? pools.familyMale : pools.familyFemale);
    const distractFam = pickOther(
      rng,
      subj === 'he' ? pools.familyMale : pools.familyFemale,
      correctFam,
    );
    const { options, answerIndex } = place(correctFam, distractFam);
    return {
      before: `${possOf(subj)} my`,
      options,
      after: '.',
      spoken: `${possOf(subj)} my ${correctFam}.`,
      answerIndex,
    };
  }
  if (kind === 'is-job-not') {
    // A: ( Are / Is ) he a teacher? → Is(挖 Is/Are)
    const subj: Subj = coin(rng) ? 'he' : 'she';
    const job = pick(rng, pools.jobs);
    const { options, answerIndex } = place('Is', 'Are');
    return {
      before: '',
      options,
      after: `${subj} a ${job}?`,
      spoken: `Is ${subj} a ${job}?`,
      answerIndex,
    };
  }
  if (kind === 'i-am-job') {
    // I ( is / am ) a cook. → am
    const job = pick(rng, pools.jobs);
    const { options, answerIndex } = place('am', 'is');
    return { before: 'I', options, after: `a ${job}.`, spoken: `I am a ${job}.`, answerIndex };
  }
  // confusable:抽一個易混淆對,造一句含正解字的句。
  const pair = pick(rng, pools.confusablePairs);
  const correct = pick(rng, pair);
  const distractor = pair[0] === correct ? pair[1] : pair[0];
  const { sentence, before, after } = sentenceForConfusable(rng, correct, pools);
  const placed = place(correct, distractor);
  return {
    before,
    options: placed.options,
    after,
    spoken: sentence,
    answerIndex: placed.answerIndex,
  };
}

/**
 * 造一句字面含 `word` 的完整句(挖 `word` 成空格)。回完整 spoken 句 + 空格前後字串。
 * 硬規則:spoken 必字面包含 word(挖空法保證)。
 */
function sentenceForConfusable(
  rng: () => number,
  word: string,
  pools: Pools,
): { sentence: string; before: string; after: string } {
  let sentence: string;
  switch (word) {
    case "Who's":
    case "What's":
      sentence = `${word} she?`;
      break;
    case 'Is':
    case 'Are': {
      const subj = word === 'Is' ? (coin(rng) ? 'he' : 'she') : 'you';
      const job = pick(rng, pools.jobs);
      sentence = `${word} ${subj} a ${job}?`;
      break;
    }
    case "he's":
    case "she's":
      sentence = `No, ${word} not.`;
      break;
    case 'am':
      sentence = 'I am a cook.';
      break;
    case 'is': {
      const subj = coin(rng) ? 'He' : 'She';
      sentence = `${subj} is a cook.`;
      break;
    }
    case 'met':
    case 'meet':
      sentence = `Nice to ${word} you.`;
      break;
    case 'he':
    case 'she':
      sentence = `Is ${word} a cook?`;
      break;
    default:
      sentence = `${word}.`;
  }
  // 以 word 為界切出 before / after(挖空)。word 在句中一定存在(上面拼出)。
  const idx = sentence.indexOf(word);
  const before = sentence.slice(0, idx).trim();
  const after = sentence.slice(idx + word.length).trim();
  return { sentence, before, after };
}

// ───────────────────────────── 組卷 ─────────────────────────────

const DEFAULT_COUNTS: Required<PartCounts> = { A: 6, B: 4, C: 5, D: 5 };

/**
 * 從池 + 範本生一份新的同型聽力卷。同 seed → 同卷(可重現)。
 *
 * @param opts seed(必填)+ 可選題數 / 池 / id / title。
 * @returns 一份 `ListeningExam`(四 Part)。生成後建議過 `validateGeneratedExam`。
 */
export function generateListeningExam(opts: ListeningGenOpts): ListeningExam {
  const rng = makeRng(opts.seed);
  const pools: Pools = { ...DEFAULT_POOLS, ...opts.pools };
  const counts: Required<PartCounts> = { ...DEFAULT_COUNTS, ...opts.counts };

  const parts: ListeningPart[] = [
    {
      kind: 'listen-check',
      label: 'Part A',
      title: 'Listen and Check',
      instructions: '仔細聽,把和聽力相同的發音字打 ✓。每題 2 分。',
      pointsEach: 2,
      items: genPartA(rng, pools, counts.A),
    },
    {
      kind: 'listen-choose',
      label: 'Part B',
      title: 'Listen and Choose',
      instructions: '仔細聽,選出與聽力相符的答。每題 3 分。',
      pointsEach: 3,
      items: genPartB(rng, pools, counts.B),
    },
    {
      kind: 'listen-mark',
      label: 'Part C',
      title: 'Listen and Mark',
      instructions: '聽到的句子與題目相同打 ○、不同打 ×。每題 2 分。',
      pointsEach: 2,
      items: genPartC(rng, pools, counts.C),
    },
    {
      kind: 'listen-circle',
      label: 'Part D',
      title: 'Listen and Circle',
      instructions: '把和聽力相同的發音字圈起來。每答 3 分。',
      pointsEach: 3,
      items: genPartD(rng, pools, counts.D),
    },
  ];

  return {
    id: opts.id ?? `listening-${opts.seed}`,
    title: opts.title ?? `英語聽力卷 · 第 ${opts.seed} 回`,
    textbook: '翰林',
    parts,
  };
}

// ───────────────────────────── 7.4 驗證器 ─────────────────────────────

/** Part A 合法字白名單 = MINIMAL_PAIRS 攤平。 */
const MINIMAL_PAIR_WORDS: ReadonlySet<string> = new Set(DEFAULT_POOLS.minimalPairs.flat());
/**
 * Part D 答案小品詞對(Yes/No;翰林原卷 D#9 用到,非 phonics 對立而是回應對)。
 * 與 CONFUSABLE_PAIRS 一起構成 Part D 合法 option 白名單。
 */
const PARTICLE_PAIRS: ReadonlyArray<readonly [string, string]> = [['Yes', 'No']];

/**
 * Part D 合法字白名單 = CONFUSABLE_PAIRS ∪ PARTICLE_PAIRS ∪ JOBS ∪ FAMILY
 * (挖空 option 可能是家人詞或回應小品詞);**大小寫不敏感**(句首 He ≡ 池內 he)。
 */
const CIRCLE_WORDS: ReadonlySet<string> = new Set(
  [
    ...DEFAULT_POOLS.confusablePairs.flat(),
    ...PARTICLE_PAIRS.flat(),
    ...DEFAULT_POOLS.jobs,
    ...DEFAULT_POOLS.familyMale,
    ...DEFAULT_POOLS.familyFemale,
  ].map((w) => w.toLowerCase()),
);
/** 男性家庭詞(性別一致檢查用)。 */
const MALE_FAMILY: ReadonlySet<string> = new Set(DEFAULT_POOLS.familyMale);
/** 女性家庭詞。 */
const FEMALE_FAMILY: ReadonlySet<string> = new Set(DEFAULT_POOLS.familyFemale);

/**
 * 驗證一份(產生或定稿)聽力卷是否合法。回**錯誤訊息清單**,空陣列 = 合法。
 *
 * 檢查(spec §7.4):
 *   - 每題 `audio.spoken` 非空。
 *   - Part A:兩 option ∈ MINIMAL_PAIRS 白名單、真對立(非同音)、spoken===options[answerIndex]。
 *   - Part B:指向式唯一(正解代名詞 = 問句指涉,且無其他選項用同代名詞)(R2)。
 *   - Part C:same → spoken===printed;diff → spoken≠printed 且句中代名詞性別==家庭詞性別(R4)。
 *   - Part D:option ∈ CONFUSABLE∪JOBS∪FAMILY 白名單;spoken 以詞邊界含 answer 且**不含 distractor**(R3)。
 */
export function validateGeneratedExam(exam: ListeningExam): string[] {
  const errors: string[] = [];
  const seenIds = new Set<string>();

  for (const part of exam.parts) {
    for (const item of part.items) {
      // 通用:id 唯一 + audio.spoken 非空。
      if (seenIds.has(item.id)) errors.push(`${item.id}: 重複的 item id`);
      seenIds.add(item.id);
      if (!item.audio || typeof item.audio.spoken !== 'string' || item.audio.spoken.trim() === '') {
        errors.push(`${item.id}: audio.spoken 為空`);
        continue;
      }
    }

    if (part.kind === 'listen-check') {
      for (const it of part.items) validateCheck(it, errors);
    } else if (part.kind === 'listen-choose') {
      for (const it of part.items) validateChoose(it, errors);
    } else if (part.kind === 'listen-mark') {
      for (const it of part.items) validateMark(it, errors);
    } else {
      for (const it of part.items) validateCircle(it, errors);
    }
  }

  return errors;
}

function validateCheck(it: ListenCheckItem, errors: string[]): void {
  if (it.options.length !== 2) errors.push(`${it.id}: Part A options 必須恰 2 個`);
  if (it.answerIndex !== 0 && it.answerIndex !== 1) {
    errors.push(`${it.id}: Part A answerIndex 非 0/1`);
    return;
  }
  if (it.options[it.answerIndex] !== it.audio.spoken) {
    errors.push(`${it.id}: Part A spoken「${it.audio.spoken}」≠ options[answerIndex]「${it.options[it.answerIndex]}」`);
  }
  if (it.options[0] === it.options[1]) {
    errors.push(`${it.id}: Part A 兩個 option 相同`);
  }
  // R1:兩字皆 ∈ MINIMAL_PAIRS 白名單(策展閉集),杜絕池外冷僻字 / 假對立。
  for (const w of it.options) {
    if (!MINIMAL_PAIR_WORDS.has(w)) {
      errors.push(`${it.id}: Part A「${w}」不在 MINIMAL_PAIRS 白名單`);
    }
  }
  // 非同音:兩字不可同音(我方池無同音對;若出現拼字不同但同音也擋)。
  if (areHomophones(it.options[0], it.options[1])) {
    errors.push(`${it.id}: Part A「${it.options[0]}/${it.options[1]}」是同音字,無法靠聽辨別`);
  }
}

/** 已知同音字黑名單(配對皆同音)。我方池無同音對;留此擋未來池外輸入。 */
const HOMOPHONES: ReadonlyArray<ReadonlySet<string>> = [
  new Set(['to', 'too', 'two']),
  new Set(['there', 'their', "they're"]),
  new Set(['hear', 'here']),
  new Set(['son', 'sun']),
  new Set(['flower', 'flour']),
  new Set(['meet', 'meat']),
];

/** 兩字是否同音(查黑名單;相同字也算「無法靠聽辨別」)。 */
function areHomophones(a: string, b: string): boolean {
  if (a.toLowerCase() === b.toLowerCase()) return true;
  const la = a.toLowerCase();
  const lb = b.toLowerCase();
  return HOMOPHONES.some((g) => g.has(la) && g.has(lb));
}

/** 從一句回應抽主詞代名詞(he / she / I);抓不到回 null。 */
function pronounOf(choice: string): 'he' | 'she' | 'I' | null {
  if (/\bI('m| am| is)?\b/.test(choice)) return 'I';
  if (/\b(he's|he is|he)\b/i.test(choice)) return 'he';
  if (/\b(she's|she is|she)\b/i.test(choice)) return 'she';
  return null;
}

function validateChoose(it: ListenChooseItem, errors: string[]): void {
  if (it.choices.length !== 3) errors.push(`${it.id}: Part B choices 必須恰 3 個`);
  if (it.answerIndex < 0 || it.answerIndex >= it.choices.length) {
    errors.push(`${it.id}: Part B answerIndex 越界`);
    return;
  }
  // 每個選項都要能抓到主詞代名詞(否則無法判唯一性)。
  const pronouns = it.choices.map(pronounOf);
  if (pronouns.some((p) => p === null)) {
    errors.push(`${it.id}: Part B 有選項抓不到主詞代名詞(${it.choices.join(' | ')})`);
    return;
  }
  // 問句要求的主詞:Is he/she…?→he/she;Are you…?→I;Who's he/she?→he/she。
  const spoken = it.audio.spoken;
  let expected: 'he' | 'she' | 'I' | null = null;
  let m: RegExpMatchArray | null;
  if ((m = spoken.match(/^Is (he|she) /i))) expected = m[1].toLowerCase() === 'he' ? 'he' : 'she';
  else if (/^Are you /i.test(spoken)) expected = 'I';
  else if ((m = spoken.match(/^Who's (he|she)\?/i))) expected = m[1].toLowerCase() === 'he' ? 'he' : 'she';
  if (!expected) {
    errors.push(`${it.id}: Part B 問句「${spoken}」非已知範本,無法判唯一解`);
    return;
  }
  // **唯一解硬規則**:三選項中「主詞代名詞 === 問句要求主詞」者必須恰 1 個,
  // 且就是 answerIndex(golden「Who's he?」= He's/She's/She's,兩個 she 干擾仍唯一)。
  const matching = pronouns
    .map((p, i) => (p === expected ? i : -1))
    .filter((i) => i >= 0);
  if (matching.length !== 1) {
    errors.push(`${it.id}: Part B 問句「${spoken}」期望主詞 ${expected},相符選項有 ${matching.length} 個(須恰 1)→ 非唯一解`);
    return;
  }
  if (matching[0] !== it.answerIndex) {
    errors.push(`${it.id}: Part B answer 指向 ${pronouns[it.answerIndex]},但唯一相符者在 index ${matching[0]}`);
  }
}

function validateMark(it: ListenMarkItem, errors: string[]): void {
  if (it.answer !== 'O' && it.answer !== 'X') {
    errors.push(`${it.id}: Part C answer 非 O/X`);
    return;
  }
  if (!it.printed || it.printed.trim() === '') errors.push(`${it.id}: Part C printed 為空`);
  if (it.answer === 'O' && it.audio.spoken !== it.printed) {
    errors.push(`${it.id}: Part C answer=○ 但 spoken≠printed`);
  }
  if (it.answer === 'X' && it.audio.spoken === it.printed) {
    errors.push(`${it.id}: Part C answer=× 但 spoken===printed(語意沒改)`);
  }
  // R4:printed 與 spoken 句中代名詞性別必與句中家庭詞性別一致(杜絕 "He's my grandma")。
  const ge1 = genderError(it.printed);
  if (ge1) errors.push(`${it.id}: Part C printed ${ge1}`);
  const ge2 = genderError(it.audio.spoken);
  if (ge2) errors.push(`${it.id}: Part C spoken ${ge2}`);
}

/**
 * 檢查一句中「代名詞性別」與「家庭詞性別」是否一致。回 null = OK,否則回錯誤描述。
 * 規則:句中若同時出現主詞代名詞(he/she/his/her/He's/She's)與家庭詞,兩者性別須相同。
 */
function genderError(sentence: string): string | null {
  const male = /\b(he|his|he's)\b/i.test(sentence);
  const female = /\b(she|her|she's)\b/i.test(sentence);
  // 句中是否出現某性別家庭詞。
  let famGender: 'male' | 'female' | null = null;
  for (const w of sentence.toLowerCase().split(/[^a-z']+/)) {
    if (MALE_FAMILY.has(w)) famGender = 'male';
    else if (FEMALE_FAMILY.has(w)) famGender = 'female';
  }
  if (famGender === null) return null; // 無家庭詞 → 無此約束。
  if (famGender === 'male' && female) return `性別矛盾:female 代名詞配 male 家庭詞「${sentence}」`;
  if (famGender === 'female' && male) return `性別矛盾:male 代名詞配 female 家庭詞「${sentence}」`;
  return null;
}

function validateCircle(it: ListenCircleItem, errors: string[]): void {
  if (it.options.length !== 2) errors.push(`${it.id}: Part D options 必須恰 2 個`);
  if (it.answerIndex !== 0 && it.answerIndex !== 1) {
    errors.push(`${it.id}: Part D answerIndex 非 0/1`);
    return;
  }
  if (it.options[0] === it.options[1]) errors.push(`${it.id}: Part D 兩個 option 相同`);
  const answerWord = it.options[it.answerIndex];
  const distractor = it.options[it.answerIndex === 0 ? 1 : 0];
  // R3 白名單:兩 option ∈ CONFUSABLE ∪ PARTICLE ∪ JOBS ∪ FAMILY(大小寫不敏感)。
  for (const w of it.options) {
    if (!CIRCLE_WORDS.has(w.toLowerCase())) {
      errors.push(`${it.id}: Part D「${w}」不在 CONFUSABLE∪JOBS∪FAMILY 白名單`);
    }
  }
  // R3 詞邊界:spoken 須以詞邊界含 answer。
  if (!containsWord(it.audio.spoken, answerWord)) {
    errors.push(`${it.id}: Part D spoken「${it.audio.spoken}」未以詞邊界含答案「${answerWord}」`);
  }
  // R3:spoken **不得**含 distractor(否則聽到的就不只答案那個字)。
  if (containsWord(it.audio.spoken, distractor)) {
    errors.push(`${it.id}: Part D spoken「${it.audio.spoken}」含了干擾字「${distractor}」(R3 違反)`);
  }
}

/**
 * `text` 是否以**詞邊界**含 `word`(大小寫敏感,避免 "he" ⊂ "she/teacher" 假命中)。
 * 邊界 = 非 [A-Za-z0-9'](保留 's 結尾如 he's/Who's 為一個詞)。
 */
function containsWord(text: string, word: string): boolean {
  const esc = word.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  return new RegExp(`(^|[^A-Za-z0-9'])${esc}([^A-Za-z0-9']|$)`).test(text);
}
